/**
 * Watrly Driver Portal — بوابة السائق
 */
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom';
import { DriverAuthProvider, useDriverAuth } from './context/DriverAuthContext';
import { DialogProvider } from '../context/DialogContext';

import LoginPage       from './pages/auth/LoginPage';
import RegisterPage    from './pages/auth/RegisterPage';
import HomePage        from './pages/home/HomePage';
import ActiveOrderPage from './pages/orders/ActiveOrderPage';
import ChatPage        from './pages/chat/ChatPage';
import ProfilePage     from './pages/profile/ProfilePage';
import ApplyPage       from './pages/apply/ApplyPage';
import VehiclesPage    from './pages/vehicles/VehiclesPage';
import './DriverApp.css';

const Logo = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="#3b82f6"/>
    <path d="M16 5 C11 10 8 14 8 19 a8 8 0 0 0 16 0 C24 14 21 10 16 5Z" fill="white"/>
  </svg>
);

const IcoHome    = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"/></svg>;
const IcoTruck   = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"/></svg>;
const IcoUser    = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/></svg>;
const IcoCar     = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124A17.9 17.9 0 0016.5 9.75l-1.5-4.5H6L4.5 9.75a17.9 17.9 0 00-1.875 4.376A1.125 1.125 0 003.75 14.25v4.5"/></svg>;
const IcoBuild   = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"/></svg>;

function DriverLayout() {
  const { driver } = useDriverAuth();
  const loc = useLocation();
  const isChat = loc.pathname.includes('/chat/');

  return (
    <div className="drv-layout">
      {/* Header */}
      <header className="drv-header">
        <div className="drv-header__brand">
          <Logo/>
          <span>Watrly</span>
          <small style={{color:'var(--drv-text2)',fontSize:11,marginRight:4}}>سائق</small>
        </div>
        <div className="drv-header__actions">
          <span className={`drv-badge drv-badge--${
            driver?.availability_status === 'online'  ? 'online'  :
            driver?.availability_status === 'busy'    ? 'busy'    :
            driver?.availability_status === 'on_break'? 'break'   : 'offline'
          }`} style={{fontSize:11}}>
            ●{' '}
            {driver?.availability_status === 'online'   ? 'متاح' :
             driver?.availability_status === 'busy'     ? 'مشغول' :
             driver?.availability_status === 'on_break' ? 'استراحة' : 'غير متاح'}
          </span>
        </div>
      </header>

      {/* Main content */}
      <main className="drv-main">
        <Routes>
          <Route path="home"         element={<HomePage/>}/>
          <Route path="order/active" element={<ActiveOrderPage/>}/>
          <Route path="chat/:orderId" element={<ChatPage/>}/>
          <Route path="profile"      element={<ProfilePage/>}/>
          <Route path="apply"        element={<ApplyPage/>}/>
          <Route path="vehicles"     element={<VehiclesPage/>}/>
          <Route path="*"            element={<Navigate to="home" replace/>}/>
        </Routes>
      </main>

      {/* Bottom nav (hide on chat page for more screen space) */}
      {!isChat && (
        <nav className="drv-nav">
          <NavLink to="/driver/home" className={({isActive})=>`drv-nav__item ${isActive?'active':''}`}>
            <IcoHome/> الرئيسية
          </NavLink>
          <NavLink to="/driver/order/active" className={({isActive})=>`drv-nav__item ${isActive?'active':''}`}>
            <IcoTruck/> طلبي
          </NavLink>
          <NavLink to="/driver/vehicles" className={({isActive})=>`drv-nav__item ${isActive?'active':''}`}>
            <IcoCar/> مركباتي
          </NavLink>
          <NavLink to="/driver/apply" className={({isActive})=>`drv-nav__item ${isActive?'active':''}`}>
            <IcoBuild/> شركات
          </NavLink>
          <NavLink to="/driver/profile" className={({isActive})=>`drv-nav__item ${isActive?'active':''}`}>
            <IcoUser/> حسابي
          </NavLink>
        </nav>
      )}
    </div>
  );
}

function Guard({ children }) {
  const { isAuthenticated, loading } = useDriverAuth();
  if (loading) return (
    <div style={{minHeight:'100vh',background:'var(--drv-bg)',display:'flex',alignItems:'center',justifyContent:'center'}} dir="rtl">
      <div className="drv-spinner"/>
    </div>
  );
  return isAuthenticated ? children : <Navigate to="/driver/login" replace/>;
}

const DriverApp = () => (
  <div className="drv-app">
    <DriverAuthProvider>
      <DialogProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/driver/login"    element={<LoginPage/>}/>
          <Route path="/driver/register" element={<RegisterPage/>}/>
          <Route
            path="/driver/*"
            element={
              <Guard>
                <DriverLayout/>
              </Guard>
            }
          />
        </Routes>
      </BrowserRouter>
      </DialogProvider>
    </DriverAuthProvider>
  </div>
);

export default DriverApp;
